<template>
  <div>
    <MobileSlider
      v-if="mobile"
      :images="images"
      @slide-changed="mediaVideo?.mute"
    >
      <template #image="mediaFragment">
        <MediaImage
          v-if="
            mediaFragment.media &&
            mediaFragment.__typename === 'ParagraphPdpStageImage'
          "
          :image="mediaFragment.media.image"
          :url="mediaFragment.media.url"
          class="h-full"
          :image-style="rokkaImageStyleMobile(mediaFragment.media)"
        />
        <!-- pointer-events-none fixes a problem that long press on the video on an Android phone leads to a broken swiper where you can not swipe left or right anymore. -->
        <MediaVideo
          v-if="
            mediaFragment.media &&
            mediaFragment.__typename === 'ParagraphPdpStageVideo'
          "
          ref="mediaVideo"
          :video="mediaFragment.media.video"
          :poster="mediaFragment.media.poster"
          class="pointer-events-none h-full"
          autoplay
          loop
          :show-sound-button="mediaFragment.sound"
          video-class="object-cover"
        />
      </template>
    </MobileSlider>
    <DesktopSlider
      v-if="!mobile && images.length > 1"
      :images="thumbnails"
      :active="active"
      @set-active="$emit('setActive', $event)"
    >
      <template #image="thumbnail">
        <button class="relative h-full w-full">
          <SpriteSymbol
            v-if="
              images[thumbnail.index]?.__typename === 'ParagraphPdpStageVideo'
            "
            name="play"
            class="absolute bottom-12 right-12 h-[1.5em] w-[1.5em] scale-150 text-grey-dark-01"
          />
          <MediaImage
            :image-style="rokkaImageStyleDesktop"
            img-class="h-full w-full bg-grey-mid object-cover"
            class="h-full w-full"
            :rokka-hash="thumbnail.rokkaHash"
            :image="thumbnail.image"
          />
        </button>
      </template>
    </DesktopSlider>
  </div>
</template>

<script setup lang="ts">
import type { MediaImageProps } from '../Media/Image/index.vue'
import MobileSlider from '@/components/Slider/MobileSlider.vue'
import DesktopSlider from '@/components/Slider/DesktopSlider.vue'
import type {
  MediaImageFragment,
  ParagraphPdpStageImageFragment,
  ParagraphPdpStageVideoFragment,
} from '#build/graphql-operations'
import type { MediaVideo } from '#build/components'
import { falsy } from '~/helpers/type'

const props = defineProps<{
  active: number
  images: (ParagraphPdpStageImageFragment | ParagraphPdpStageVideoFragment)[]
  mobile?: boolean
}>()

defineEmits<{
  setActive: [idx: number]
}>()

const rokkaImageStyleDesktop = defineImageStyle({
  type: 'sizes',
  aspectRatio: 1,
  sizes: {
    sm: 180,
  },
})

const thumbnails = computed<MediaImageProps[]>(() =>
  props.images
    .map((pdPStageMediaFragment): MediaImageProps | undefined => {
      if (pdPStageMediaFragment?.media?.__typename === 'MediaVideo') {
        return pdPStageMediaFragment.media.poster
          ? pdPStageMediaFragment.media.poster
          : { rokkaHash: pdPStageMediaFragment.media.video?.file?.rokka?.hash }
      } else {
        return pdPStageMediaFragment.media
      }
    })
    .filter(falsy),
)

const mediaVideo = ref<typeof MediaVideo>()

const rokkaImageStyleMobile = (mediaImageFragment: MediaImageFragment) => {
  const image = mediaImageFragment.image
  const imageWidth = image?.width
  const imageHeight = image?.height
  const imageAspectRatio =
    imageWidth && imageHeight ? imageWidth / imageHeight : undefined
  const imageAspectRatioRelativToWindow =
    imageWidth && imageHeight
      ? `${imageWidth} / ${imageHeight * 2}` // height * 2 since we are only display 50% of vh.
      : undefined

  if (!imageAspectRatio || !imageAspectRatioRelativToWindow || !imageHeight) {
    return undefined
  }

  // mobile is only <= lg (= 1024px width)
  return defineImageStyleOris({
    type: 'pictures',
    pictures: [
      // width 0 - 600
      {
        media: `(max-width: 600px) and (min-aspect-ratio: ${imageAspectRatioRelativToWindow})`,
        width: 600,
      },
      // width 600 - max (1024)
      {
        media: `(min-aspect-ratio: ${imageAspectRatioRelativToWindow})`,
        width: 1024,
      },
      // height 0 - 700
      {
        media: `(max-height: 700px) and (max-aspect-ratio: ${imageAspectRatioRelativToWindow})`,
        height: 350,
        aspectRatio: imageAspectRatio,
      },
      // height 700 - 1400
      {
        media: `(max-height: 1400px) and (max-aspect-ratio: ${imageAspectRatioRelativToWindow})`,
        height: 700,
        aspectRatio: imageAspectRatio,
      },
      // height 1400 - max (and default)
      {
        viewport: 'fallback',
        height: imageHeight,
        aspectRatio: imageAspectRatio,
      },
    ],
  })
}
</script>

<style lang="scss" scoped>
:deep(.paragraph-video-volume-icon) {
  bottom: 50px !important;
}
</style>
